/// <amd-module name="Test/TestPlansHubRefresh/TestCasesImportModule/Util/TestCaseCSVImportUtility" />
import * as Resources from "Test/TestPlansHubRefresh/TestCasesImportModule/Resources";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";

/**
 * Utility to read CSV and convert the data to rows and columns object
 */
export class TestCaseCSVImportUtility {
    // bareHeader means a header that has been stripped of its number and lowercased
    private _requiredHeadersMap: { [bareHeader: string]: string };
    private _idHeader: string;
    private c_lineFeedChar = "\n";
    private c_carriageReturnChar = "\r";
    private _requiredHeadersOrder: string[];

    constructor() {
        this._requiredHeadersMap = {};
    }

    public async importCSVFile(
        csv: string,
        pageContext: IVssPageContext,
        plan: any,
        suite: any,
        callBack: (percentComplete: number, testCasesUpdated: number, testCasesCreated: number, errorMessage ?: string) => void,
        errorCallBack: (error: string) => void,
        fileType: string
    ) {
        if (Object.keys(this._requiredHeadersMap).length === 0) {
            this._populateHeaders();
        }
        const contents: string[][] = this._parseCSVFile(csv);
        this._trimContents(contents);
        this._validateHeaders(contents);
        this._validateRequiredHeadersOrder(contents);
        this._validateRequiredFields(contents);

        const legacyService = pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");

        legacyService.requireModules(["TestManagement/Scripts/TFS.TestManagement.TestCaseImportHelper"]).then(([testCaseImportHelper]) => {
            const importHelper = new testCaseImportHelper.TestCaseImportHelper();
            suite.type = suite.suiteType;
            importHelper.initialize(plan, suite, fileType, (isInitialized: boolean) => {
                if (isInitialized) {
                    importHelper.convertCSVToTestCasesData(contents, callBack, errorCallBack);
                }
            });
        });
    }

    private _populateHeaders() {
        this._requiredHeadersMap[Resources.Title] = Resources.Title;
        this._requiredHeadersMap[Resources.WorkItemType] = Resources.WorkItemType;
        this._requiredHeadersMap[Resources.Id] = Resources.Id;
        this._requiredHeadersMap[Resources.TestStep] = Resources.TestStep;
        this._requiredHeadersMap[Resources.StepAction] = Resources.StepAction;
        this._requiredHeadersMap[Resources.StepExpected] = Resources.StepExpected;
        this._idHeader = Resources.Id;

        this._requiredHeadersOrder = [
            Resources.Id,
            Resources.WorkItemType,
            Resources.Title,
            Resources.TestStep,
            Resources.StepAction,
            Resources.StepExpected
        ];
    }

    private _parseCSVFile(csv: string): string[][] {
        if (csv.length === 0) {
            throw `Empty dataset found.`;
        }

        // Append on newline because it signals the end of the last row
        if (csv.slice(-1) !== this.c_lineFeedChar) {
            csv += this.c_lineFeedChar;
        }

        const contents = [];
        let currentRow = [];
        let inToken = false;
        let hasQuotes = false;
        let token = "";

        for (let i = 0; i < csv.length; i++) {
            const c = csv[i];
            const isLineFeedEnding = c === this.c_lineFeedChar;
            const isCarriageReturnEnding = c === this.c_carriageReturnChar && i + 1 < csv.length && csv[i + 1] === this.c_lineFeedChar;
            const isLineBreakChar = isLineFeedEnding || isCarriageReturnEnding;

            // Append token
            if ((c === "," && !inToken) || (c === "," && inToken && !hasQuotes)) {
                currentRow.push(token.trim());
                token = "";
                inToken = false;
                hasQuotes = false;
            }
            // End of the line - append row
            else if ((isLineBreakChar && !inToken) || (isLineBreakChar && inToken && !hasQuotes)) {
                // Flush token, if exists
                if (token !== "") {
                    currentRow.push(token.trim());
                    token = "";
                }

                // Data shape Error: Number of fields in the current row exceeds the number of column headers
                if (contents.length > 0 && contents[0].length < currentRow.length) {
                    throw `Excess number of field values found at line ${contents.length + 1}: expected ${contents[0].length} but read ${
                        currentRow.length
                    }.`;
                }
                // Must pad row because of fewer field values than number of column headers
                else if (contents.length > 0 && contents[0].length > currentRow.length) {
                    for (let i = currentRow.length; i < contents[0].length; i++) {
                        currentRow.push("");
                    }
                }

                // Skip over LF
                if (isCarriageReturnEnding) {
                    i++;
                }
                if (!this._isRowEmpty(currentRow)) {
                    contents.push(currentRow);
                }
                currentRow = [];
                inToken = false;
                hasQuotes = false;
            }
            // Start of double-quoted value
            else if (c === '"' && !inToken) {
                inToken = true;
                hasQuotes = true;
            }
            // Quote found in middle of double-quoted value
            else if (c === '"' && inToken && hasQuotes && i + 1 < csv.length && csv[i + 1] === '"') {
                token += '"';
                i++; // skip next iteration
            }
            // End of double-quoted value
            else if (c === '"' && inToken && hasQuotes && i + 1 < csv.length && csv[i + 1] !== '"') {
                inToken = false;
                hasQuotes = false;

                // Syntax Error: Ended CSV value too early
                if (
                    csv[i + 1] !== "," &&
                    csv[i + 1] !== this.c_lineFeedChar &&
                    !(csv[i + 1] === this.c_carriageReturnChar && i + 2 < csv.length && csv[i + 2] === this.c_lineFeedChar)
                ) {
                    throw `Double-quoted value possibly terminated too early or missing delimiter at line ${contents.length + 1}, col ${
                        currentRow.length + 1
                    }.`;
                }
            }
            // Syntax Error: Random " that is not enclosed in quotes.
            else if (c === '"' && inToken && !hasQuotes) {
                throw `Double-quote not enclosed in double-quotes at line ${contents.length + 1}, col ${currentRow.length + 1}.`;
            }
            // Otherwise, append character
            else {
                inToken = true;
                token += c;
            }
        }

        // Syntax Error: Enclosing value in quotes did not terminate.
        if (hasQuotes == true) {
            throw `Non-terminating double-quotes at line ${contents.length + 1}, col ${currentRow.length + 1}.`;
        }

        return contents;
    }

    // Handle edge case where CSV can have empty rows / column on the end of the file.
    // Remove the extra rows/columns to streamline validation & reading the contents
    private _trimContents(contents: string[][]): void {
        // trim rows
        let danglingRowIndex = -1;
        for (let i = contents.length - 1; i > 0 && this._isRowEmpty(contents[i]); i--) {
            danglingRowIndex = i;
        }
        if (danglingRowIndex > 0) {
            contents.splice(danglingRowIndex);
        }

        // trim columns
        let danglingColIndex = -1;
        for (let i = contents[0].length - 1; i > 0 && this._isColumnEmpty(i, contents); i--) {
            danglingColIndex = i;
        }
        if (danglingColIndex > 0) {
            contents.forEach((row) => row.splice(danglingColIndex));
        }
    }

    private _validateHeaders(contents: string[][]): void {
        if (contents.length === 0) {
            throw `Empty headers in the file`;
        }

        // Remove leading and trailing white spaces for column headers
        const headers = (contents[0] = contents[0].map((header) => header.trim()));
        const requiredIndicesMap = this._getIndicesMap(headers, this._requiredHeadersMap);

        let emptyColumnHeaderIndices = this._getEmptyColumnHeaderIndices(headers);
        if (emptyColumnHeaderIndices.length > 0) {
            // If an entire column is empty, display separately for clarity
            const emptyColumnIndices = emptyColumnHeaderIndices.filter((index) => this._isColumnEmpty(index, contents));
            emptyColumnHeaderIndices = emptyColumnHeaderIndices.filter((index) => !this._isColumnEmpty(index, contents));

            if (emptyColumnHeaderIndices.length > 0) {
                throw `Empty headers columns exist in the file`;
            }
            if (emptyColumnIndices.length > 0) {
                throw `Empty columns exist in the file`;
            }
        }

        const missingRequiredHeaders = this._getMissingRequiredHeaders(requiredIndicesMap);
        if (missingRequiredHeaders.length > 0) {
            throw `Missing one or more required headers: ${this._requiredHeadersOrder.join(",")}`;
        }

        // ie. "Title 1, Title 2, Work Item Type 1, Work Item Type 2"
        const duplicateRequiredHeaders = this._getDuplicateRequiredHeaders(requiredIndicesMap);
        if (duplicateRequiredHeaders.length > 0) {
            throw `Duplicate required headers Id, WorkItem Type or Title present`;
        }

        // ie. "Title 2, Title 1" OR "Title 1, ID, Title 2"
        const nonConsecutiveTreeHeaderIndices = this._getNonConsecutiveTreeHeaderIndices(requiredIndicesMap, headers);
        if (nonConsecutiveTreeHeaderIndices.length > 0) {
            throw `Non consecutive tree headers present`;
        }
    }

    // Returns a dictionary that maps stripped, lowercased required header names within headersMap to their indices within headers
    private _getIndicesMap(headers: string[], headersMap: { [bareHeader: string]: string }): { [bareHeader: string]: number[] } {
        const result: { [bareHeader: string]: number[] } = {};
        const importantHeaders: string[] = Object.keys(headersMap);
        importantHeaders.forEach((header) => {
            header = header.toLocaleLowerCase();
            result[header] = []; // Array initialization for tree view import compatibility: "Title 1, Title 2, Title 3, ..." => [ i, i+1, i+2, ... ]
        });

        //Indices pushed in ASC order
        headers.forEach((header, index) => {
            header = this._stripNumberSuffix(header).toLocaleLowerCase();
            if (!result[header]) {
                return;
            }

            result[header].push(index);
        });

        return result;
    }

    private _getEmptyColumnHeaderIndices(headers: string[]): number[] {
        const result: number[] = [];
        headers.forEach((header, index) => {
            if (header.length === 0) {
                result.push(index);
            }
        });

        return result;
    }

    private _isColumnEmpty(index: number, contents: string[][]): boolean {
        for (let i = 0; i < contents.length; i++) {
            const cell = contents[i][index];
            if (cell && cell.length > 0) {
                return false;
            }
        }
        return true;
    }

    private _isRowEmpty(row: string[]): boolean {
        for (let i = 0; i < row.length; i++) {
            if (row[i] && row[i].length > 0) {
                return false;
            }
        }
        return true;
    }

    // Returns an array of requiredHeaders that are not found in headers
    private _getMissingRequiredHeaders(requiredIndicesMap: { [bareHeader: string]: number[] }): string[] {
        const missingRequiredHeaders = Object.keys(requiredIndicesMap).filter((header) => requiredIndicesMap[header].length === 0);

        // Return original header names
        return missingRequiredHeaders.map((header) => this._requiredHeadersMap[header]);
    }

    private _getDuplicateRequiredHeaders(requiredIndicesMap: { [bareHeader: string]: number[] }): string[] {
        const result = this._getDuplicateHeaders(requiredIndicesMap, this._requiredHeadersMap);

        // Return if there are multiple duplicates: BOTH 'Title 1', Title 2' and 'Work Item Type 1', 'Work Item Type 2'
        return result.length > 1 ? result : [];
    }

    private _getDuplicateHeaders(indicesMap: { [bareHeader: string]: number[] }, headersMap: { [bareHeader: string]: string }): string[] {
        const result: string[] = [];
        const headers = Object.keys(indicesMap);

        headers.forEach((header) => {
            const indices = indicesMap[header];
            if (indices.length > 1) {
                result.push(headersMap[header]);
            }
        });

        return result;
    }

    private _getNonConsecutiveTreeHeaderIndices(requiredIndicesMap: { [bareHeader: string]: number[] }, headers: string[]): number[] {
        const indicesArr = Object.keys(requiredIndicesMap).map((header) => requiredIndicesMap[header]);
        const result: number[] = [];

        for (let i = 0; i < indicesArr.length; i++) {
            const indices = indicesArr[i];

            // Skip singular column headers - we'll let the user use singular 'Title 1' or 'Title 2' and strip the number later
            if (indices.length === 1) {
                continue;
            }

            for (let j = 0; j < indices.length; j++) {
                const index = indices[j];
                const numberSuffix = this._getNumberSuffix(headers[index]);

                // If not consecutive in number or spot, return indices
                if (numberSuffix !== j + 1 || (j > 0 && indices[j] - 1 !== indices[j - 1])) {
                    result.concat(indices.slice()); // Return all instances for now
                }
            }
        }

        return result;
    }

    // Strips the number suffix for Tree View Import: "Title 1" -> "Title"
    private _stripNumberSuffix(header: string): string {
        let result = header;
        const parts = header.split(" ");

        if (parts.length > 0 && Number(parts[parts.length - 1])) {
            result = parts.slice(0, -1).join(" ");
        }

        return result;
    }

    // Returns the number suffix if it exists or -1 otherwise: "Title 1" -> 1
    private _getNumberSuffix(header: string): number {
        let result = -1;
        const parts = header.split(" ");

        if (parts.length > 0 && Number(parts[parts.length - 1])) {
            result = Number(parts[parts.length - 1]);
        }

        return result;
    }

    private _validateRequiredHeadersOrder(contents: string[][]): void {
        const headers = (contents[0] = contents[0].map((header) => header.trim()));
        const requiredHeaders = headers.slice(0, this._requiredHeadersOrder.length);
        if (requiredHeaders.join(",").toLowerCase() !== this._requiredHeadersOrder.join(",").toLowerCase()) {
            throw `Required headers must be of order: ${this._requiredHeadersOrder.join(",")}`;
        }
    }

    // Assumes column headers are validated already
    private _validateRequiredFields(contents: string[][]): void {
        if (contents.length === 0) {
            return;
        }

        const errorMessages = [];
        const headers = contents[0];
        const idIndex = this._getHeaderIndex(headers, this._idHeader);
        const requiredIndicesMap = this._getIndicesMap(headers, this._requiredHeadersMap);
        const idsMap: { [bareHeader: string]: number[] } = {};
        let workItemTypeErrorIndices = [];
        let maxIndentationAllowed = 1;

        for (let i = 1; i < contents.length; i++) {
            const csvRow = contents[i].map((field) => field.trim());

            // Cache ids and record their row indices
            const id = idIndex >= 0 && csvRow[idIndex].length > 0 ? Number(csvRow[idIndex]) : null;

            const workItemTypeIndex = this._getHeaderIndex(headers, Resources.WorkItemType);
            const workItemType = workItemTypeIndex >= 0 && csvRow[workItemTypeIndex].length > 0 ? csvRow[workItemTypeIndex].trim() : "";

            const titleIndex = this._getHeaderIndex(headers, Resources.Title);
            const title = titleIndex >= 0 && csvRow[titleIndex].length > 0 ? csvRow[titleIndex].trim() : "";

            const errorRow = this._validateWorkItemTypeField(workItemType, i, id, title);
            if (errorRow > 0) {
                workItemTypeErrorIndices.push(errorRow);
            }
            if (id && id != 0 && workItemType.toLowerCase() != Resources.SharedSteps) {
                idsMap[id] = idsMap[id] || [];
                idsMap[id].push(i);
            } else if (id && id === 0) {
                errorMessages.push(`Invalid ${this._idHeader} 0 at line ${i + 1}, col ${idIndex + 1}.`);
            } else if (id !== null && isNaN(id)) {
                errorMessages.push(`Non-integer ${this._idHeader} at line ${i + 1}, col ${idIndex + 1}.`);
            }

            // Validate field values that belong under the required headers
            const requiredHeaders = Object.keys(requiredIndicesMap);
            requiredHeaders.forEach((requiredHeader) => {
                const indices = requiredIndicesMap[requiredHeader];
                let numFieldsFound = 0;
                let currentIndentation = 0;
                // Only one field value must be non-empty
                indices.forEach((index, indentation) => {
                    if (csvRow[index].length > 0) {
                        currentIndentation = indentation;
                        numFieldsFound++;
                    }
                });
                // Validate for proper tree structure
                if (numFieldsFound === 1 && indices.length > 1) {
                    if (currentIndentation > maxIndentationAllowed) {
                        errorMessages.push(
                            `Field values that do not adhere to correct tree structure at line ${i + 1}, cols ${indices
                                .map((index) => index + 1)
                                .join(", ")}.`
                        );
                    } else {
                        maxIndentationAllowed = currentIndentation + 1;
                    }
                }
            });
        }

        if (workItemTypeErrorIndices.length > 0) {
            throw `Found invalid work item types at lines ${workItemTypeErrorIndices.join(",")}`;
        }

        // Log duplicate ids
        const ids = Object.keys(idsMap);
        ids.forEach((id) => {
            const rows = idsMap[id];
            if (rows.length > 1) {
                errorMessages.push(`Duplicate ${this._idHeader} for ${id} at lines ${rows.map((index) => index + 1).join(", ")}.`);
            }
        });

        if (errorMessages.length > 0) {
            throw `Found field value error(s):\n${errorMessages.join("\n")}`;
        }
    }

    private _getHeaderIndex(headers: string[], headerName: string): number {
        headerName = headerName.toLocaleLowerCase();
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i].toLocaleLowerCase();
            if (header === headerName) {
                return i;
            }
        }

        return -1;
    }

    private _validateWorkItemTypeField(workItemType: string, index: number, id: number | null, title: string): number {
        // in case of test case row
        if (title !== "" && workItemType.toLowerCase() === Resources.TestCase.toLowerCase()) {
            return -1;
        }
        // in case of normal step row
        if (!id && title === "" && workItemType === "") {
            return -1;
        }
        // in case of shared step row
        if (id && title === "" && workItemType.toLowerCase() === Resources.SharedSteps.toLowerCase()) {
            return -1;
        }
        return index + 1;
    }
}
